import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Marker } from "react-leaflet";
import { Copy, MapPinOff, MessageSquare, Phone } from "lucide-react";
import OsmMap from "@/components/map/OsmMap";
import { RecenterMap } from "@/components/map/RecenterMap";
import { createBrandedPinIcon } from "@/lib/mapIcons";
import { AddressHint } from "@/components/ui/AddressHint";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import {
  contactLink,
  orderByPreference,
  readPreferredChannel,
  writePreferredChannel,
  type ContactChannel,
} from "@/features/orders/lib/contactChannels";
import { LocalitySection } from "@/features/orders/components/LocalitySection";
import { translateGeocodeErrorCode } from "@/features/orders/lib/geocodeFailureBreakdown";
import { hasValidCoords } from "@/features/orders/lib/locality";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/cn";
import type { Order } from "@/types/orders";

interface Props {
  order: Order;
  /**
   * Let the map absorb the column's leftover height instead of standing at its
   * fixed `h-44` (mockup, 2026-08-25: the tracking box must finish on the same
   * line as the other two columns).
   *
   * This is the SAME opt-in mechanism {@link AddressMapBlock} uses in the create
   * and edit forms, deliberately rather than a second one: the map is the only
   * element in the column with no intrinsic height, so it is the honest place to
   * put the slack. Growing anything else would mean inventing height for a
   * field that does not want it.
   */
  growMap?: boolean;
}

interface ChannelOption {
  channel: ContactChannel;
  /** Menu entry text. */
  labelKey: string;
  /** Bare app name, used in the confirmation copy («Открыть Viber?»). */
  appNameKey: string;
}

/**
 * Menu contents, in their default order. The remembered channel is hoisted to
 * the top at render time — these arrays are never mutated, so the order does not
 * drift as the component re-renders.
 */
const CALL_CHANNELS: readonly ChannelOption[] = [
  {
    channel: "system",
    labelKey: "orders.detail.callVia.system",
    appNameKey: "orders.detail.appName.system",
  },
  {
    channel: "whatsapp",
    labelKey: "orders.detail.callVia.whatsapp",
    appNameKey: "orders.detail.appName.whatsapp",
  },
  {
    channel: "telegram",
    labelKey: "orders.detail.callVia.telegram",
    appNameKey: "orders.detail.appName.telegram",
  },
  {
    channel: "viber",
    labelKey: "orders.detail.callVia.viber",
    appNameKey: "orders.detail.appName.viber",
  },
];

const MESSAGE_CHANNELS: readonly ChannelOption[] = [
  {
    channel: "whatsapp",
    labelKey: "orders.detail.messageVia.whatsapp",
    appNameKey: "orders.detail.appName.whatsapp",
  },
  {
    channel: "telegram",
    labelKey: "orders.detail.messageVia.telegram",
    appNameKey: "orders.detail.appName.telegram",
  },
  {
    channel: "viber",
    labelKey: "orders.detail.messageVia.viber",
    appNameKey: "orders.detail.appName.viber",
  },
  {
    channel: "sms",
    labelKey: "orders.detail.messageVia.sms",
    appNameKey: "orders.detail.appName.sms",
  },
];

/**
 * «КУДА» — the first column of the order detail modal: address, a read-only
 * map, and the three contact/copy actions.
 *
 * The actions are **disabled, never hidden**, when there is no phone on file —
 * TASK-4822's established rule, which the orders row menu already follows. A
 * control that disappears makes a dispatcher wonder whether the feature exists
 * at all; a disabled one says "not for this order". Because a disabled button
 * is not focusable and so can never show a hover tooltip to a keyboard user,
 * the reason is also rendered as `sr-only` text inside the button.
 */
export function OrderViewWhere({ order, growMap = false }: Props) {
  const { t } = useTranslation();

  const phone = (order.customer_phone ?? "").trim();
  const hasPhone = phone.length > 0;

  /** Pending app-scheme hand-off awaiting our own confirmation. */
  const [confirmChannel, setConfirmChannel] = useState<{
    channel: ContactChannel;
    appName: string;
  } | null>(null);
  /**
   * Last channel used, hoisted to the top of both menus. Seeded from storage on
   * first render (not in an effect — an effect would render the default order
   * once and then reshuffle it under the cursor).
   */
  const [preferred, setPreferred] = useState<ContactChannel | null>(() =>
    readPreferredChannel(),
  );
  const address = (order.address ?? "").trim();
  const hasAddress = address.length > 0;

  const latNum = parseFloat(order.lat ?? "");
  const lonNum = parseFloat(order.lon ?? "");
  /**
   * `0,0` counts as "not set" — the app-wide convention. Without it a blank
   * coordinate drops a pin in the Gulf of Guinea and asserts a location this
   * order does not have. One shared rule (`hasValidCoords`), also used by the
   * locality predicate (Sprint 228).
   */
  const hasCoords = hasValidCoords(order.lat, order.lon);

  async function handleCopyAddress() {
    try {
      await navigator.clipboard.writeText(address);
      toastSuccess(t("orders.detail.addressCopied"));
    } catch {
      toastError(t("orders.detail.addressCopyFailed"));
    }
  }

  async function handleCopyPhone() {
    try {
      await navigator.clipboard.writeText(phone);
      toastSuccess(t("orders.detail.phoneCopied"));
    } catch {
      toastError(t("orders.detail.phoneCopyFailed"));
    }
  }

  /**
   * Follow a channel, confirming FIRST when the browser would raise its own
   * "Open <app>?" prompt (owner, 2026-08-24).
   *
   * `https://` channels open straight away — there is no system prompt to warn
   * about, and an extra click on the common path would be pure friction.
   *
   * `noopener` makes `window.open` return **null** even on success, so the return
   * value says nothing about whether it worked — do not branch on it. A blocked
   * popup is silent; «Скопировать номер» in the same menu is the always-works
   * fallback, which is why every menu carries one.
   */
  function handleChannel(channel: ContactChannel, appName: string) {
    const { url, needsAppConfirm } = contactLink(channel, phone);
    if (needsAppConfirm) {
      setConfirmChannel({ channel, appName });
      return;
    }
    writePreferredChannel(channel);
    setPreferred(channel);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    /*
      `growMap` turns the column into a full-height flex column so the map can
      claim the leftover with `flex-1`. `gap-3` reproduces `space-y-3` exactly,
      so the non-growing layout keeps the spacing it always had.
    */
    <div
      className={cn(
        // From `lg` only (columns side by side): stacked at 375px, `flex-1
        // min-h-0` squeezed 334px of content into 103px (Sprint 228 UI review).
        growMap ? "flex flex-col gap-3 lg:min-h-0 lg:flex-1" : "space-y-3",
      )}
    >
      {/*
        The address wraps rather than truncating: half an address is useless to a
        dispatcher, where an ellipsised NAME is still an identifier.

        Copying lives HERE, as an icon beside the address (owner, 2026-08-25:
        «кнопку Копировать убрать, сделать над картой где адрес … значок
        копировать адрес»). This supersedes the 2026-08-25 note that previously
        stood here and argued the opposite — that the labelled «Копировать»
        button in the row below should be the one to survive.

        Both of that note's constraints still hold and are still satisfied:

        1. EXACTLY ONE copy affordance in this column. The labelled button is
           removed from the row below, which now holds «Позвонить» and
           «Написать» only. Two controls announcing as copy actions is the
           duplicate-accessible-name defect (BUG-504/508).
        2. The affordance is VISIBLE. The reason the address-as-click-target
           version was replaced on 2026-08-24 is that it had no visual cue. This
           is a real `<button>` with a permanent icon and the accessible name
           «Копировать адрес» — not a click handler on the text.

        `items-start` rather than `items-center`: the address can wrap to two or
        three lines and the icon must stay level with the FIRST line, not drift
        to the vertical middle of the block.
      */}
      <div className="flex items-start gap-2">
        <p className="min-w-0 flex-1 break-words text-base font-medium text-gray-900">
          {hasAddress ? address : "—"}
        </p>
        {/* Disabled (never hidden) with no address, so the column's shape does
            not change between orders; the reason rides in the accessible name
            because a disabled control cannot be focused to reveal a tooltip. */}
        <IconButton
          icon={<Copy className="h-4 w-4" aria-hidden="true" />}
          /* `IconButton` uses its label as BOTH the aria-label and the tooltip,
             and has no `disabledReason` of its own — so the reason is folded
             into the name when the control is unavailable. A disabled button
             cannot be focused, so a reason shown only on hover never reaches a
             keyboard user. */
          label={
            hasAddress
              ? t("orders.detail.copyAddress")
              : `${t("orders.detail.copyAddress")} — ${t("orders.detail.noAddressReason")}`
          }
          disabled={!hasAddress}
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={() => void handleCopyAddress()}
        />
      </div>

      {order.geocode_failed && (
        <div className="space-y-1">
          <span className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md bg-brand-warning/15 px-2 py-0.5 text-xs font-semibold text-brand-warning">
            <MapPinOff className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t("orders.geocodeFailed")}
          </span>
          {/* TASK-4745: the reason comes first because it decides which of the
              two opposite actions — fix the address, or retry — is worth
              taking at all. */}
          <p className="text-xs font-medium text-brand-warning">
            {translateGeocodeErrorCode(order.geocode_error_code, t)}
          </p>
          <p className="text-xs text-gray-500">
            {t("orders.geocodeFailedHint")}
          </p>
          <AddressHint className="mt-1.5" />
        </div>
      )}

      {/* Sprint 228 — the locality (НП) line, directly under the address and the
          geocode note so the two problem notes sit together above the map.
          Hidden without coordinates. */}
      <LocalitySection
        target={{ kind: "order", id: order.id }}
        locality={order.locality}
        status={order.locality_status}
        distanceM={order.locality_distance_m}
        hasCoords={hasCoords}
      />

      {hasCoords ? (
        <div
          className={cn(
            "w-full overflow-hidden rounded-xl border border-gray-100",
            // Growing: a floor of the old fixed height so the map is never
            // SMALLER than it was, then `flex-1` for the column's spare space.
            growMap ? "min-h-[11rem] flex-1" : "h-44",
          )}
        >
          <OsmMap center={[latNum, lonNum]} zoom={14} className="h-full w-full">
            <RecenterMap lat={latNum} lon={lonNum} />
            {/* Read-only: the pin is NOT draggable in view mode. */}
            <Marker position={[latNum, lonNum]} icon={createBrandedPinIcon()} />
          </OsmMap>
        </div>
      ) : (
        /* Never a map centred on a default city — that would assert a location
           the order does not have (§5.2). */
        <div
          className={cn(
            "flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 text-center",
            // The placeholder grows by the same rule as the map it stands in
            // for — otherwise the columns would align only once an address
            // resolves, and jump apart again on an order without coordinates.
            growMap ? "min-h-[11rem] flex-1" : "h-44",
          )}
        >
          <MapPinOff className="h-6 w-6 text-gray-300" aria-hidden="true" />
          <p className="text-xs text-gray-500">{t("orders.detail.noCoords")}</p>
        </div>
      )}

      {/*
        Owner, 2026-08-24: the dispatcher works at a COMPUTER. `tel:` and `sms:`
        hand the number to whatever the OS registered — on a Mac that is Messages,
        which cannot place a call at all, so «Позвонить» looked broken and
        «Написать» opened the wrong app. Neither button is removed (a desk phone /
        softphone handler is a real setup); each now offers the choice instead of
        assuming one. Both menus put «Скопировать номер» last — the option that
        always works, whatever is installed.

        WhatsApp/Telegram/Viber take the number digits-only (`wa.me` rejects
        `+`/spaces); the `tel:`/`sms:` entries keep the raw string, which those
        handlers accept.
      */}
      {/* TWO columns (owner, 2026-08-25): «Позвонить» · «Написать». Copying the
          address moved to the icon beside the address itself, so the third
          column is gone; each remaining button gets half the width instead of a
          third, which is why the labels no longer risk truncating here. */}
      <div className="grid grid-cols-2 gap-2">
        <DropdownMenu
          align="start"
          trigger={
            <ActionButton
              icon={<Phone className="h-4 w-4" aria-hidden="true" />}
              label={t("orders.detail.call")}
              disabled={!hasPhone}
              disabledReason={t("orders.detail.noPhoneReason")}
            />
          }
          items={[
            ...orderByPreference(CALL_CHANNELS, preferred).map((c) => ({
              key: `call-${c.channel}`,
              label: t(c.labelKey),
              onSelect: () => handleChannel(c.channel, t(c.appNameKey)),
            })),
            {
              key: "call-copy",
              label: t("orders.detail.copyPhone"),
              onSelect: () => void handleCopyPhone(),
            },
          ]}
        />
        <DropdownMenu
          align="start"
          trigger={
            <ActionButton
              icon={<MessageSquare className="h-4 w-4" aria-hidden="true" />}
              label={t("orders.detail.message")}
              disabled={!hasPhone}
              disabledReason={t("orders.detail.noPhoneReason")}
            />
          }
          items={[
            ...orderByPreference(MESSAGE_CHANNELS, preferred).map((c) => ({
              key: `msg-${c.channel}`,
              label: t(c.labelKey),
              onSelect: () => handleChannel(c.channel, t(c.appNameKey)),
            })),
            {
              key: "msg-copy",
              label: t("orders.detail.copyPhone"),
              onSelect: () => void handleCopyPhone(),
            },
          ]}
        />
      </div>

      {/*
        Our own warning, shown BEFORE a custom-scheme link is followed.

        The browser's «Open Viber?» prompt is owned by Chrome: a page cannot
        restyle, suppress or even detect it. What we CAN do is stop it being a
        surprise — name the app and the number first, and offer «Скопировать
        номер» as the way out for a dispatcher who does not have that messenger
        installed. `https://` channels (WhatsApp, Telegram) raise no prompt at all
        and are therefore opened directly, without this step.
      */}
      <ConfirmDialog
        open={confirmChannel !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmChannel(null);
        }}
        title={t("orders.detail.openApp.title", {
          app: confirmChannel?.appName ?? "",
        })}
        description={t("orders.detail.openApp.description", {
          app: confirmChannel?.appName ?? "",
          phone,
        })}
        confirmLabel={t("orders.detail.openApp.confirm")}
        cancelLabel={t("orders.detail.openApp.copyInstead")}
        onConfirm={() => {
          if (!confirmChannel) return;
          const { channel } = confirmChannel;
          writePreferredChannel(channel);
          setPreferred(channel);
          const { url } = contactLink(channel, phone);
          // A custom scheme must be a same-tab navigation: `window.open` on
          // `viber://` leaves a blank tab behind when the handler takes over.
          window.location.href = url;
          setConfirmChannel(null);
        }}
        onCancel={() => {
          void handleCopyPhone();
          setConfirmChannel(null);
        }}
      />
    </div>
  );
}

interface ActionButtonProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "disabled"> {
  icon: React.ReactNode;
  label: string;
  disabled: boolean;
  /** Why the control is unavailable — announced, not merely shown on hover. */
  disabledReason: string;
}

/**
 * Forwards its ref and spreads the rest of its props (BUG-356 / TASK-4836).
 *
 * Both are required for the call/message variants, where this button is a Radix
 * `DropdownMenu.Trigger asChild` child: Radix clones the child and injects its own
 * ref plus pointer handlers. A component that swallows them renders a button that
 * simply never opens the menu — and nothing errors, so it looks like a dead
 * control. The plain «Копировать» usage passes `onClick` through the same spread.
 */
const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton(
    { icon, label, disabled, disabledReason, className, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-disabled={disabled || undefined}
        title={disabled ? disabledReason : undefined}
        {...props}
        className={cn(
          // Three-to-a-row, «Копировать» is the longest label in either locale
          // and ellipsised at 1280 with the old `px-2 gap-1.5`. The ICON hides
          // below the `wide` (1440px) breakpoint rather than the LABEL
          // truncating: an icon beside a clipped word carries less than the
          // whole word alone, and the 40px height and touch target are
          // unaffected either way. `wide`, not `xl` — `xl` IS 1280, so an
          // `xl:` rule is already active at the width that needed the room.
          "inline-flex min-h-[40px] items-center justify-center gap-1 rounded-xl border border-gray-200 px-1.5 py-2",
          "[&>svg]:hidden wide:[&>svg]:block",
          "text-sm font-semibold text-gray-700 transition-colors",
          disabled
            ? "cursor-not-allowed opacity-40"
            : "bg-white hover:bg-gray-50",
          className,
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
        {/* A disabled button cannot be focused, so a hover-only tooltip never
          reaches a keyboard or screen-reader user. The reason travels with the
          accessible name instead. */}
        {disabled && <span className="sr-only">— {disabledReason}</span>}
      </button>
    );
  },
);
